import { useCallback, useEffect, useState } from "react";
import {
  AlertCircle,
  BarChart2,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  Download,
  Gauge,
  MessageSquare,
  RefreshCw,
  ThumbsDown,
  ThumbsUp,
  Timer,
  Wrench,
} from "lucide-react";
import {
  downloadConversationsCsv,
  fetchConversations,
  fetchFeedbackReport,
  fetchPerformanceReport,
  type ConversationFilter,
  type ConversationRecord,
  type ConversationsResponse,
  type FeedbackReport,
  type PerformanceReport,
} from "@/lib/api";
import { useBrowserAuth } from "@/hooks/useBrowserAuth";
import { AuthCheckingScreen, SignInPage } from "@/components/SignInPage";
import { useTheme } from "@/hooks/useTheme";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/Button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { cn } from "@/lib/cn";

const REFRESH_INTERVAL_MS = 60_000;

type Tab = "overview" | "conversations" | "performance";

function isAccessDeniedMessage(message: string): boolean {
  const lower = message.toLowerCase();
  return (
    lower.includes("admin dashboard") ||
    lower.includes("admin_emails") ||
    lower.includes("sign in required") ||
    lower.includes("do not have access")
  );
}

/**
 * Admin dashboard entry — enforces the same Easy Auth browser login wall as
 * the main chat app before loading any admin APIs.
 */
export function AdminDashboard() {
  const authState = useBrowserAuth();
  useTheme();

  if (authState === "checking") return <AuthCheckingScreen />;
  if (authState === "unauthenticated") return <SignInPage />;

  return <AdminDashboardContent />;
}

function AdminDashboardContent() {
  const [tab, setTab] = useState<Tab>("overview");
  const [report, setReport] = useState<FeedbackReport | null>(null);
  const [convData, setConvData] = useState<ConversationsResponse | null>(null);
  const [perfData, setPerfData] = useState<PerformanceReport | null>(null);
  const [convPage, setConvPage] = useState(1);
  const [convFilter, setConvFilter] = useState<ConversationFilter>("all");
  const [userQuery, setUserQuery] = useState("");
  const [userDraft, setUserDraft] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [forbiddenMessage, setForbiddenMessage] = useState<string | null>(null);
  const [lastRefreshed, setLastRefreshed] = useState<Date | null>(null);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);

  const loadOverview = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchFeedbackReport();
      setReport(data);
      setLastRefreshed(new Date());
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to load report";
      if (isAccessDeniedMessage(message)) setForbiddenMessage(message);
      else setError(message);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadConversations = useCallback(
    async (page: number, filter: ConversationFilter, user: string) => {
      setLoading(true);
      setError(null);
      try {
        const data = await fetchConversations(page, filter, user);
        setConvData(data);
        setLastRefreshed(new Date());
      } catch (err) {
        const message = err instanceof Error ? err.message : "Failed to load conversations";
        if (isAccessDeniedMessage(message)) setForbiddenMessage(message);
        else setError(message);
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const loadPerformance = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchPerformanceReport();
      setPerfData(data);
      setLastRefreshed(new Date());
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to load performance";
      if (isAccessDeniedMessage(message)) setForbiddenMessage(message);
      else setError(message);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleExport = async () => {
    setExporting(true);
    setExportError(null);
    try {
      await downloadConversationsCsv();
    } catch (err) {
      setExportError(err instanceof Error ? err.message : "Failed to export CSV");
    } finally {
      setExporting(false);
    }
  };

  const refreshCurrentTab = () => {
    if (tab === "overview") return void loadOverview();
    if (tab === "performance") return void loadPerformance();
    return void loadConversations(convPage, convFilter, userQuery);
  };

  useEffect(() => {
    if (tab === "overview") {
      void loadOverview();
      const timer = setInterval(() => void loadOverview(), REFRESH_INTERVAL_MS);
      return () => clearInterval(timer);
    }
    if (tab === "performance") {
      void loadPerformance();
      const timer = setInterval(() => void loadPerformance(), REFRESH_INTERVAL_MS);
      return () => clearInterval(timer);
    }
    void loadConversations(convPage, convFilter, userQuery);
  }, [tab, convPage, convFilter, userQuery, loadOverview, loadConversations, loadPerformance]);

  const handleFilterChange = (f: ConversationFilter) => {
    setConvFilter(f);
    setConvPage(1);
  };

  const applyUserQuery = (value: string) => {
    setUserDraft(value);
    setUserQuery(value.trim());
    setConvPage(1);
  };

  const positiveColor =
    report && report.upPct >= 70
      ? "success"
      : report && report.upPct >= 40
        ? "warning"
        : report
          ? "error"
          : undefined;

  if (forbiddenMessage) {
    const needsConfig = forbiddenMessage.toLowerCase().includes("not configured");
    const needsSignIn = forbiddenMessage.toLowerCase().includes("sign in");
    return (
      <div className="flex min-h-screen items-center justify-center bg-[var(--bg)] px-6">
        <div className="max-w-md text-center">
          <AlertCircle className="mx-auto mb-3 h-8 w-8 text-text-muted" />
          <h1 className="text-[16px] font-semibold text-text">
            {needsConfig
              ? "Admin dashboard is not configured"
              : needsSignIn
                ? "Sign in required"
                : "You don't have access to this page"}
          </h1>
          <p className="mt-2 text-[13px] leading-relaxed text-text-muted">{forbiddenMessage}</p>
          {needsConfig ? (
            <p className="mt-3 text-[12px] leading-relaxed text-text-muted">
              Ask whoever manages the App Service to set the{" "}
              <code className="rounded bg-surface-muted px-1 py-0.5 text-[11px]">ADMIN_EMAILS</code>{" "}
              setting to your work email, then restart the app.
            </p>
          ) : null}
          <div className="mt-4 flex flex-wrap items-center justify-center gap-3">
            {needsSignIn ? (
              <a
                href={`/.auth/login/aad?post_login_redirect_url=${encodeURIComponent("/admin/feedback")}`}
                className="text-[13px] text-accent transition-colors hover:underline"
              >
                Sign in with Microsoft
              </a>
            ) : null}
            <a
              href="/"
              className="text-[13px] text-text-muted transition-colors hover:text-text"
            >
              ← Back to chat
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--bg)]">
      <header className="sticky top-0 z-10 flex h-[52px] items-center border-b border-border bg-surface px-6">
        <div className="flex items-center gap-2.5">
          <img
            src="/ndc-logo.svg"
            alt="National Datacare"
            className="h-7 w-auto select-none dark:hidden"
            draggable={false}
          />
          <img
            src="/ndc-logo-white.svg"
            alt=""
            className="hidden h-7 w-auto select-none dark:block"
            draggable={false}
          />
          <span aria-hidden className="h-5 w-px bg-border" />
          <span className="text-[12px] font-medium text-text-muted">Pilot Dashboard</span>
        </div>

        <div className="ml-auto flex items-center gap-3">
          {lastRefreshed && !loading && (
            <span className="hidden text-[11px] text-text-muted sm:inline">
              Updated {lastRefreshed.toLocaleTimeString()}
            </span>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => void handleExport()}
            disabled={exporting}
            aria-label="Export conversations as CSV"
            title="Download every conversation and its feedback as a CSV file"
            className="gap-1.5 text-text-muted hover:text-text"
          >
            <Download className="h-3.5 w-3.5" />
            <span className="hidden text-[12px] sm:inline">
              {exporting ? "Exporting…" : "Export CSV"}
            </span>
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={refreshCurrentTab}
            disabled={loading}
            aria-label="Refresh"
            className="gap-1.5 text-text-muted hover:text-text"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
            <span className="hidden text-[12px] sm:inline">Refresh</span>
          </Button>
          <a href="/" className="text-[12px] text-text-muted transition-colors hover:text-text">
            ← Back to chat
          </a>
        </div>
      </header>

      <Tabs
        value={tab}
        onValueChange={(value) => setTab(value as Tab)}
        className="flex min-h-0 flex-1 flex-col"
      >
        <div className="border-b border-border bg-surface px-6">
          <TabsList className="h-auto w-full justify-start gap-0 rounded-none bg-transparent p-0">
            <TabsTrigger
              value="overview"
              className="gap-1.5 rounded-none border-b-2 border-transparent px-4 py-3 text-[13px] shadow-none data-[state=active]:border-[var(--button)] data-[state=active]:bg-transparent data-[state=active]:text-text data-[state=active]:shadow-none"
            >
              <BarChart2 className="h-3.5 w-3.5" />
              Overview
            </TabsTrigger>
            <TabsTrigger
              value="conversations"
              className="gap-1.5 rounded-none border-b-2 border-transparent px-4 py-3 text-[13px] shadow-none data-[state=active]:border-[var(--button)] data-[state=active]:bg-transparent data-[state=active]:text-text data-[state=active]:shadow-none"
            >
              <MessageSquare className="h-3.5 w-3.5" />
              All conversations
            </TabsTrigger>
            <TabsTrigger
              value="performance"
              className="gap-1.5 rounded-none border-b-2 border-transparent px-4 py-3 text-[13px] shadow-none data-[state=active]:border-[var(--button)] data-[state=active]:bg-transparent data-[state=active]:text-text data-[state=active]:shadow-none"
            >
              <Gauge className="h-3.5 w-3.5" />
              Performance
            </TabsTrigger>
          </TabsList>
        </div>

        <main className="mx-auto w-full max-w-6xl px-6 py-8">
          {error && (
            <Card className="mb-6 border-[var(--error)]/30 bg-[var(--error-soft)] shadow-none">
              <CardContent className="flex items-start gap-3 p-4 text-[13px] text-[var(--error)]">
                <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                <span>{error}</span>
              </CardContent>
            </Card>
          )}

          {exportError && (
            <Card className="mb-6 border-[var(--error)]/30 bg-[var(--error-soft)] shadow-none">
              <CardContent className="flex items-start gap-3 p-4 text-[13px] text-[var(--error)]">
                <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                <span>Couldn't export CSV: {exportError}</span>
              </CardContent>
            </Card>
          )}

          <TabsContent value="overview" className="mt-0 focus-visible:ring-0">
            <OverviewTab report={report} loading={loading} positiveColor={positiveColor} />
          </TabsContent>

          <TabsContent value="conversations" className="mt-0 focus-visible:ring-0">
            <ConversationsTab
              data={convData}
              loading={loading}
              filter={convFilter}
              page={convPage}
              userDraft={userDraft}
              userQuery={userQuery}
              onFilterChange={handleFilterChange}
              onPageChange={setConvPage}
              onUserDraftChange={setUserDraft}
              onUserApply={applyUserQuery}
            />
          </TabsContent>

          <TabsContent value="performance" className="mt-0 focus-visible:ring-0">
            <PerformanceTab data={perfData} loading={loading} />
          </TabsContent>
        </main>
      </Tabs>
    </div>
  );
}

// ─── Overview tab ────────────────────────────────────────────────────────────

interface OverviewTabProps {
  report: FeedbackReport | null;
  loading: boolean;
  positiveColor?: "success" | "warning" | "error";
}

function formatDuration(ms: number | null | undefined): string {
  if (!Number.isFinite(ms) || ms == null || ms < 0) return "—";
  if (ms < 1000) return `${Math.round(ms)} ms`;
  return `${(ms / 1000).toFixed(ms >= 10_000 ? 0 : 1)} s`;
}

function OverviewTab({ report, loading, positiveColor }: OverviewTabProps) {
  const latency = report?.latency;
  return (
    <>
      <div className="mb-6">
        <h1 className="text-[22px] font-semibold text-text">Response Quality</h1>
        <p className="mt-1 text-[13px] text-text-muted">
          Thumbs up / down votes and response times from the pilot
        </p>
      </div>

      {report?.note && (
        <Card className="mb-6 border-[var(--warning)]/30 bg-[var(--warning)]/10 shadow-none">
          <CardContent className="flex items-start gap-3 p-4 text-[13px] text-[var(--warning)]">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>{report.note}</span>
          </CardContent>
        </Card>
      )}

      <div className="mb-8 grid grid-cols-1 gap-4 sm:grid-cols-3">
        <StatCard
          icon={<BarChart2 className="h-5 w-5" />}
          label="Total votes"
          value={loading ? "—" : String(report?.total ?? 0)}
        />
        <StatCard
          icon={<ThumbsUp className="h-5 w-5" />}
          label="Positive rate"
          value={loading ? "—" : `${report?.upPct ?? 0}%`}
          color={positiveColor}
        />
        <StatCard
          icon={<ThumbsDown className="h-5 w-5" />}
          label="Thumbs down"
          value={loading ? "—" : String(report?.downCount ?? 0)}
          color={report && report.downCount > 0 ? "error" : undefined}
        />
      </div>

      <div className="mb-8 grid grid-cols-1 gap-4 sm:grid-cols-3">
        <StatCard
          icon={<Timer className="h-5 w-5" />}
          label="Median response"
          value={loading ? "—" : formatDuration(latency?.medianMs)}
        />
        <StatCard
          icon={<Timer className="h-5 w-5" />}
          label="95th percentile"
          value={loading ? "—" : formatDuration(latency?.p95Ms)}
        />
        <StatCard
          icon={<Timer className="h-5 w-5" />}
          label="Avg response"
          value={loading ? "—" : formatDuration(latency?.avgMs)}
        />
      </div>

      {latency && !loading ? (
        <p className="mb-8 -mt-4 text-[12px] text-text-muted">
          {`Based on ${latency.sampleCount} conversation${latency.sampleCount === 1 ? "" : "s"} with recorded timing`}
        </p>
      ) : null}

      <section>
        <h2 className="mb-3 text-[11px] font-semibold uppercase tracking-wider text-text-muted">
          Top questions that got thumbs down
        </h2>

        {loading && (
          <Card className="shadow-none">
            <CardContent className="p-10 text-center text-[13px] text-text-muted">
              Loading…
            </CardContent>
          </Card>
        )}

        {!loading && (!report?.topDownQuestions || report.topDownQuestions.length === 0) && (
          <Card className="shadow-none">
            <CardContent className="p-10 text-center text-[13px] text-text-muted">
              No thumbs-down votes recorded yet.
            </CardContent>
          </Card>
        )}

        {!loading && report && report.topDownQuestions.length > 0 && (
          <Card className="overflow-hidden shadow-none">
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead className="w-10">#</TableHead>
                  <TableHead>Question</TableHead>
                  <TableHead className="w-24 text-right">Times</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {report.topDownQuestions.map((row, i) => (
                  <TableRow key={i}>
                    <TableCell className="text-text-muted">{i + 1}</TableCell>
                    <TableCell>{row.question}</TableCell>
                    <TableCell className="text-right">
                      <Badge variant="error">{row.count}</Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        )}
      </section>
    </>
  );
}

// ─── Performance tab ──────────────────────────────────────────────────────────

interface PerformanceTabProps {
  data: PerformanceReport | null;
  loading: boolean;
}

function PerformanceTab({ data, loading }: PerformanceTabProps) {
  const timedBuckets = (data?.buckets ?? []).filter((b) => b.id !== "unknown");
  const maxBucket = Math.max(1, ...timedBuckets.map((b) => b.count));
  const unknownBucket = data?.buckets.find((b) => b.id === "unknown");

  return (
    <>
      <div className="mb-6">
        <h1 className="text-[22px] font-semibold text-text">Performance &amp; tools</h1>
        <p className="mt-1 text-[13px] text-text-muted">
          How long answers take, which tools run most often, and where time is spent
        </p>
      </div>

      {data && !data.configured && !loading ? (
        <Card className="mb-6 border-[var(--warning)]/30 bg-[var(--warning)]/10 shadow-none">
          <CardContent className="flex items-start gap-3 p-4 text-[13px] text-[var(--warning)]">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>
              Conversation logging is not yet configured, so performance numbers may be empty.
              Set <strong>AZURE_STORAGE_CONNECTION_STRING</strong> (production) or{" "}
              <strong>CONVERSATION_LOG_PATH</strong> for local file logging.
            </span>
          </CardContent>
        </Card>
      ) : null}

      <div className="mb-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          icon={<Timer className="h-5 w-5" />}
          label="Median response"
          value={loading ? "—" : formatDuration(data?.latency?.medianMs)}
        />
        <StatCard
          icon={<Timer className="h-5 w-5" />}
          label="95th percentile"
          value={loading ? "—" : formatDuration(data?.latency?.p95Ms)}
        />
        <StatCard
          icon={<Wrench className="h-5 w-5" />}
          label="Avg tools / answer"
          value={loading ? "—" : String(data?.totals.avgToolsPerConversation ?? 0)}
        />
        <StatCard
          icon={<AlertCircle className="h-5 w-5" />}
          label="Tool error rate"
          value={loading ? "—" : `${data?.totals.toolErrorRatePct ?? 0}%`}
          color={
            data && data.totals.toolErrorRatePct > 10
              ? "error"
              : data && data.totals.toolErrorRatePct > 0
                ? "warning"
                : undefined
          }
        />
      </div>

      <div className="mb-8 grid grid-cols-1 gap-4 sm:grid-cols-3">
        <StatCard
          icon={<BarChart2 className="h-5 w-5" />}
          label="Conversations"
          value={loading ? "—" : String(data?.totals.conversationCount ?? 0)}
        />
        <StatCard
          icon={<Gauge className="h-5 w-5" />}
          label="Avg model rounds"
          value={
            loading
              ? "—"
              : data?.rounds.avgFoundryRounds != null
                ? String(data.rounds.avgFoundryRounds)
                : "—"
          }
        />
        <StatCard
          icon={<Wrench className="h-5 w-5" />}
          label="Answers with sources"
          value={loading ? "—" : `${data?.totals.withSourcesPct ?? 0}%`}
        />
      </div>

      <section className="mb-8">
        <h2 className="mb-3 text-[11px] font-semibold uppercase tracking-wider text-text-muted">
          Response time distribution
        </h2>
        {loading ? (
          <Card className="shadow-none">
            <CardContent className="p-10 text-center text-[13px] text-text-muted">
              Loading…
            </CardContent>
          </Card>
        ) : !data || data.withTimingCount === 0 ? (
          <Card className="shadow-none">
            <CardContent className="p-10 text-center text-[13px] text-text-muted">
              No response timings recorded yet. New answers will show up here once timing is stored.
            </CardContent>
          </Card>
        ) : (
          <Card className="shadow-none">
            <CardContent className="space-y-3 p-5">
              {timedBuckets.map((bucket) => {
                const pct = Math.round((bucket.count / maxBucket) * 100);
                return (
                  <div key={bucket.id} className="grid grid-cols-[120px_1fr_40px] items-center gap-3">
                    <span className="text-[12px] text-text-muted">{bucket.label}</span>
                    <div className="h-2.5 overflow-hidden rounded-full bg-surface-muted">
                      <div
                        className="h-full rounded-full bg-[var(--button)] transition-all"
                        style={{ width: `${pct}%` }}
                        title={`${bucket.count} conversation${bucket.count === 1 ? "" : "s"}`}
                      />
                    </div>
                    <span className="text-right text-[12px] tabular-nums text-text">{bucket.count}</span>
                  </div>
                );
              })}
              {unknownBucket && unknownBucket.count > 0 ? (
                <p className="pt-1 text-[12px] text-text-muted">
                  {unknownBucket.count} conversation{unknownBucket.count === 1 ? "" : "s"} logged
                  without timing (older rows before this was tracked).
                </p>
              ) : null}
            </CardContent>
          </Card>
        )}
      </section>

      <section className="mb-8">
        <h2 className="mb-3 text-[11px] font-semibold uppercase tracking-wider text-text-muted">
          Tool usage
        </h2>
        {loading ? (
          <Card className="shadow-none">
            <CardContent className="p-10 text-center text-[13px] text-text-muted">
              Loading…
            </CardContent>
          </Card>
        ) : !data || data.tools.length === 0 ? (
          <Card className="shadow-none">
            <CardContent className="p-10 text-center text-[13px] text-text-muted">
              No tool activity recorded yet.
            </CardContent>
          </Card>
        ) : (
          <Card className="overflow-hidden shadow-none">
            <Table className="min-w-[640px]">
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead>Tool</TableHead>
                  <TableHead className="text-right">Calls</TableHead>
                  <TableHead className="text-right">Errors</TableHead>
                  <TableHead className="text-right">Error %</TableHead>
                  <TableHead className="text-right">Avg time</TableHead>
                  <TableHead className="text-right">p95 time</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.tools.map((row) => (
                  <TableRow key={row.name}>
                    <TableCell className="font-mono text-[12px]">{row.name}</TableCell>
                    <TableCell className="text-right tabular-nums">{row.callCount}</TableCell>
                    <TableCell className="text-right tabular-nums text-text-muted">
                      {row.errorCount}
                    </TableCell>
                    <TableCell className="text-right">
                      <Badge variant={row.errorRatePct > 0 ? "error" : "secondary"}>
                        {row.errorRatePct}%
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right tabular-nums text-text-muted">
                      {formatDuration(row.avgDurationMs)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums text-text-muted">
                      {formatDuration(row.p95DurationMs)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        )}
      </section>

      <section>
        <h2 className="mb-3 text-[11px] font-semibold uppercase tracking-wider text-text-muted">
          Model &amp; tool rounds
        </h2>
        {loading ? (
          <Card className="shadow-none">
            <CardContent className="p-10 text-center text-[13px] text-text-muted">
              Loading…
            </CardContent>
          </Card>
        ) : (
          <Card className="overflow-hidden shadow-none">
            <Table>
              <TableBody>
                <TableRow>
                  <TableCell className="text-text-muted">Average model rounds</TableCell>
                  <TableCell className="text-right tabular-nums">
                    {data?.rounds.avgFoundryRounds ?? "—"}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell className="text-text-muted">Max model rounds</TableCell>
                  <TableCell className="text-right tabular-nums">
                    {data?.rounds.maxFoundryRounds ?? "—"}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell className="text-text-muted">Average tool rounds</TableCell>
                  <TableCell className="text-right tabular-nums">
                    {data?.rounds.avgToolIterations ?? "—"}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell className="text-text-muted">Max tool rounds</TableCell>
                  <TableCell className="text-right tabular-nums">
                    {data?.rounds.maxToolIterations ?? "—"}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </Card>
        )}
      </section>
    </>
  );
}

// ─── Conversations tab ────────────────────────────────────────────────────────

const FILTER_OPTS: { value: ConversationFilter; label: string }[] = [
  { value: "all", label: "All" },
  { value: "up", label: "Thumbs up" },
  { value: "down", label: "Thumbs down" },
  { value: "none", label: "No vote" },
];

interface ConversationsTabProps {
  data: ConversationsResponse | null;
  loading: boolean;
  filter: ConversationFilter;
  page: number;
  userDraft: string;
  userQuery: string;
  onFilterChange: (f: ConversationFilter) => void;
  onPageChange: (p: number) => void;
  onUserDraftChange: (value: string) => void;
  onUserApply: (value: string) => void;
}

function ConversationsTab({
  data,
  loading,
  filter,
  page,
  userDraft,
  userQuery,
  onFilterChange,
  onPageChange,
  onUserDraftChange,
  onUserApply,
}: ConversationsTabProps) {
  const users = data?.users ?? [];

  return (
    <>
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-[22px] font-semibold text-text">All Conversations</h1>
          <p className="mt-1 text-[13px] text-text-muted">
            Every question and answer from the pilot in a table — expand a row for the full answer
          </p>
        </div>

        {/* Summary pills */}
        {data && (
          <div className="flex flex-wrap gap-2 text-[12px]">
            <Badge variant="secondary">Total {data.summary.total}</Badge>
            <Badge variant="success" className="gap-1">
              <ThumbsUp className="h-3 w-3" />
              {data.summary.upCount}
            </Badge>
            <Badge variant="error" className="gap-1">
              <ThumbsDown className="h-3 w-3" />
              {data.summary.downCount}
            </Badge>
            <Badge variant="secondary">No vote {data.summary.noVoteCount}</Badge>
          </div>
        )}
      </div>

      {data && !data.configured && (
        <Card className="mb-6 border-[var(--warning)]/30 bg-[var(--warning)]/10 shadow-none">
          <CardContent className="flex items-start gap-3 p-4 text-[13px] text-[var(--warning)]">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>
              Conversation logging is not yet configured. Set{" "}
              <strong>AZURE_STORAGE_CONNECTION_STRING</strong> on the App Service (production)
              so Q&amp;A rows land in the <code className="text-[11px]">conversations</code> and{" "}
              <code className="text-[11px]">feedback</code> tables. For local-only file fallback,
              set <strong>CONVERSATION_LOG_PATH</strong> / <strong>FEEDBACK_LOG_PATH</strong>.
            </span>
          </CardContent>
        </Card>
      )}

      {/* User filter */}
      <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-center">
        <label className="sr-only" htmlFor="admin-user-filter">
          Filter by email or name
        </label>
        <input
          id="admin-user-filter"
          type="search"
          list="admin-user-options"
          value={userDraft}
          placeholder="Filter by email or name"
          onChange={(e) => onUserDraftChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") onUserApply(userDraft);
          }}
          className="w-full rounded-md border border-border bg-surface px-3 py-2 text-[13px] text-text placeholder:text-text-muted focus:border-border-strong focus:outline-none sm:max-w-xs"
        />
        <datalist id="admin-user-options">
          {users.map((u) => {
            const value = u.userEmail || u.userName || u.userId || "";
            const label = [u.userName, u.userEmail].filter(Boolean).join(" · ");
            return <option key={value} value={value} label={label || value} />;
          })}
        </datalist>
        <div className="flex gap-2">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => onUserApply(userDraft)}
            className="text-[12px]"
          >
            Apply
          </Button>
          {userQuery ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => onUserApply("")}
              className="text-[12px] text-text-muted"
            >
              Clear user
            </Button>
          ) : null}
        </div>
        {userQuery ? (
          <span className="text-[12px] text-text-muted">
            Showing {data?.total ?? 0} match{(data?.total ?? 0) === 1 ? "" : "es"} for “{userQuery}”
          </span>
        ) : null}
      </div>

      <div className="mb-4 flex flex-wrap gap-1.5">
        {FILTER_OPTS.map((opt) => (
          <Button
            key={opt.value}
            type="button"
            size="sm"
            variant={filter === opt.value ? "secondary" : "ghost"}
            onClick={() => onFilterChange(opt.value)}
            className={cn(
              "h-8 rounded-full px-3 text-[12px]",
              filter === opt.value
                ? "bg-surface-muted text-text"
                : "text-text-muted hover:text-text",
            )}
          >
            {opt.label}
          </Button>
        ))}
      </div>

      {loading && (
        <Card className="shadow-none">
          <CardContent className="p-10 text-center text-[13px] text-text-muted">
            Loading…
          </CardContent>
        </Card>
      )}

      {!loading && data && data.records.length === 0 && (
        <Card className="shadow-none">
          <CardContent className="p-10 text-center text-[13px] text-text-muted">
            No conversations recorded yet.
            {(filter !== "all" || userQuery) && " Try changing the filters."}
          </CardContent>
        </Card>
      )}

      {!loading && data && data.records.length > 0 && (
        <>
          <Card className="overflow-hidden shadow-none">
            <Table className="min-w-[920px]">
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead className="w-10 px-3" aria-label="Expand" />
                  <TableHead className="whitespace-nowrap px-3">When</TableHead>
                  <TableHead className="px-3">User</TableHead>
                  <TableHead className="px-3">Question</TableHead>
                  <TableHead className="whitespace-nowrap px-3">Vote</TableHead>
                  <TableHead className="whitespace-nowrap px-3 text-right">Latency</TableHead>
                  <TableHead className="whitespace-nowrap px-3 text-right">Tools</TableHead>
                  <TableHead className="whitespace-nowrap px-3 text-right">Sources</TableHead>
                  <TableHead className="px-3">Reason</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.records.map((rec) => (
                  <ConversationTableRows key={rec.requestId} record={rec} />
                ))}
              </TableBody>
            </Table>
          </Card>

          {/* Pagination */}
          {data.totalPages > 1 && (
            <div className="mt-6 flex items-center justify-between">
              <Button
                variant="ghost"
                size="sm"
                disabled={page <= 1}
                onClick={() => onPageChange(page - 1)}
                className="gap-1 text-[12px]"
              >
                <ChevronLeft className="h-4 w-4" />
                Previous
              </Button>
              <span className="text-[12px] text-text-muted">
                Page {data.page} of {data.totalPages}
              </span>
              <Button
                variant="ghost"
                size="sm"
                disabled={page >= data.totalPages}
                onClick={() => onPageChange(page + 1)}
                className="gap-1 text-[12px]"
              >
                Next
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          )}
        </>
      )}
    </>
  );
}

// ─── Conversation table rows ──────────────────────────────────────────────────

function ConversationTableRows({ record }: { record: ConversationRecord }) {
  const [expanded, setExpanded] = useState(false);

  const timeStr = new Date(record.timestamp).toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

  const whoLabel =
    record.userName ||
    record.userEmail ||
    (record.userId && record.userId !== "anonymous" ? record.userId : null) ||
    "Unknown user";
  const showEmail =
    Boolean(record.userEmail) &&
    record.userEmail !== record.userName &&
    whoLabel !== record.userEmail;

  const toolSummary = record.toolSummary ?? [];

  return (
    <>
      <TableRow className={cn(expanded && "bg-surface-muted/30")}>
        <TableCell className="px-2">
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            aria-expanded={expanded}
            aria-label={expanded ? "Collapse answer" : "Expand answer"}
            className="rounded p-1 text-text-muted transition-colors hover:text-text"
          >
            {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </button>
        </TableCell>
        <TableCell className="whitespace-nowrap px-3 text-text-muted">{timeStr}</TableCell>
        <TableCell className="max-w-[140px] px-3">
          <div className="truncate font-medium" title={whoLabel}>
            {whoLabel}
          </div>
          {showEmail ? (
            <div className="truncate text-[11px] text-text-muted" title={record.userEmail ?? undefined}>
              {record.userEmail}
            </div>
          ) : null}
        </TableCell>
        <TableCell className="max-w-[280px] px-3">
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="line-clamp-2 w-full text-left font-medium hover:underline"
            title={record.question}
          >
            {record.question}
          </button>
        </TableCell>
        <TableCell className="px-3">
          <VoteBadge vote={record.vote} compact />
        </TableCell>
        <TableCell className="whitespace-nowrap px-3 text-right tabular-nums">
          {formatDuration(record.durationMs)}
        </TableCell>
        <TableCell className="whitespace-nowrap px-3 text-right tabular-nums text-text-muted">
          {record.toolCallCount}
        </TableCell>
        <TableCell className="whitespace-nowrap px-3 text-right tabular-nums text-text-muted">
          {record.sourceCount}
        </TableCell>
        <TableCell className="max-w-[160px] px-3">
          {record.reason ? (
            <span
              className="line-clamp-2 text-[12px] text-[var(--error)]"
              title={record.reason}
            >
              {record.reason}
            </span>
          ) : (
            <span className="text-text-muted">—</span>
          )}
        </TableCell>
      </TableRow>
      {expanded ? (
        <TableRow className="bg-surface-muted/20 hover:bg-surface-muted/20">
          <TableCell colSpan={9} className="px-4 py-4">
            <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wider text-text-muted">
              Answer
            </p>
            <p className="whitespace-pre-wrap text-[13px] leading-relaxed text-text">
              {record.answer || "—"}
            </p>
            {toolSummary.length > 0 ? (
              <div className="mt-4">
                <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wider text-text-muted">
                  Tools used
                </p>
                <Card className="overflow-hidden shadow-none">
                  <Table className="text-[12px]">
                    <TableHeader>
                      <TableRow className="hover:bg-transparent">
                        <TableHead className="h-8 px-3 text-[11px] normal-case tracking-normal">
                          Tool
                        </TableHead>
                        <TableHead className="h-8 px-3 text-right text-[11px] normal-case tracking-normal">
                          Time
                        </TableHead>
                        <TableHead className="h-8 px-3 text-[11px] normal-case tracking-normal">
                          Status
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {toolSummary.map((t, i) => (
                        <TableRow key={`${t.name}-${i}`}>
                          <TableCell className="px-3 py-1.5 font-mono">{t.name}</TableCell>
                          <TableCell className="px-3 py-1.5 text-right tabular-nums text-text-muted">
                            {formatDuration(t.durationMs)}
                          </TableCell>
                          <TableCell className="px-3 py-1.5">
                            <Badge variant={t.error ? "error" : "success"}>
                              {t.error ? "Error" : "OK"}
                            </Badge>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Card>
              </div>
            ) : null}
            {(record.foundryRounds != null || record.toolIterations != null) && (
              <p className="mt-3 text-[11px] text-text-muted">
                {record.foundryRounds != null ? `Model rounds: ${record.foundryRounds}` : null}
                {record.foundryRounds != null && record.toolIterations != null ? " · " : null}
                {record.toolIterations != null ? `Tool rounds: ${record.toolIterations}` : null}
              </p>
            )}
          </TableCell>
        </TableRow>
      ) : null}
    </>
  );
}

// ─── Small helpers ────────────────────────────────────────────────────────────

function VoteBadge({ vote, compact }: { vote: "up" | "down" | null; compact?: boolean }) {
  if (vote === "up") {
    return (
      <Badge
        variant="success"
        className={cn("shrink-0 gap-0 p-1.5", compact ? "p-1" : "mt-0.5")}
        title="Thumbs up"
      >
        <ThumbsUp className="h-3 w-3" />
      </Badge>
    );
  }
  if (vote === "down") {
    return (
      <Badge
        variant="error"
        className={cn("shrink-0 gap-0 p-1.5", compact ? "p-1" : "mt-0.5")}
        title="Thumbs down"
      >
        <ThumbsDown className="h-3 w-3" />
      </Badge>
    );
  }
  return (
    <Badge
      variant="secondary"
      className={cn("shrink-0 gap-0 p-1.5", compact ? "p-1" : "mt-0.5")}
      title="No vote"
    >
      <MessageSquare className="h-3 w-3" />
    </Badge>
  );
}

interface StatCardProps {
  icon: React.ReactNode;
  label: string;
  value: string;
  color?: "success" | "warning" | "error";
}

function StatCard({ icon, label, value, color }: StatCardProps) {
  const valueClass =
    color === "success"
      ? "text-[var(--success)]"
      : color === "warning"
        ? "text-[var(--warning)]"
        : color === "error"
          ? "text-[var(--error)]"
          : "text-text";

  return (
    <Card className="shadow-none">
      <CardHeader className="flex flex-row items-center gap-2 space-y-0 p-5 pb-3 text-text-muted">
        {icon}
        <CardTitle className="text-[11px] font-semibold uppercase tracking-wider text-text-muted">
          {label}
        </CardTitle>
      </CardHeader>
      <CardContent className={`p-5 pt-0 text-[32px] font-semibold leading-none tabular-nums ${valueClass}`}>
        {value}
      </CardContent>
    </Card>
  );
}
